import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, CheckCheck, ChevronRight, Megaphone, Volume2, VolumeX, X } from "lucide-react";
import api from "@/lib/api";
import { fmtMYT } from "@/lib/myt";
import { getSoundEnabled, hasUserInteracted, playNotificationChime, setSoundEnabled } from "@/lib/notificationSound";
import { disablePush, enablePush, getPushState } from "@/lib/webPush";

const API = process.env.REACT_APP_BACKEND_URL;

export default function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [soundOn, setSoundOn] = useState(getSoundEnabled());
  const [push, setPush] = useState({ support: "supported", subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState("");
  const prevCount = useRef(null);
  const timerRef = useRef(null);

  // one grouped chime per poll cycle when the unread count rises (new arrivals);
  // never for already-seen items, never twice for the same notification, and
  // only after the member has interacted with the page (browser autoplay rule)
  const refreshCount = useCallback(() => {
    api.get("/notifications/unread-count")
      .then((res) => {
        const next = res.data.count || 0;
        if (prevCount.current !== null && next > prevCount.current) {
          playNotificationChime();
        }
        prevCount.current = next;
        setCount(next);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshCount();
    getPushState().then(setPush).catch(() => {});
    // Pause the unread-count poll while the tab/PWA is hidden; resume with one
    // controlled refresh when it becomes visible. Guarded so hide/show cycles
    // never stack duplicate intervals; listener + timer cleaned up on unmount.
    const start = () => { if (!timerRef.current) timerRef.current = setInterval(refreshCount, 30000); };
    const stop = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
    const onVis = () => { if (document.hidden) stop(); else { refreshCount(); start(); } };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [refreshCount]);

  // While the bell panel is open, scope away the support launcher so it can never
  // cover notification rows/controls; fully reversed on close/unmount.
  useEffect(() => {
    if (open) document.body.classList.add("notif-panel-open");
    else document.body.classList.remove("notif-panel-open");
    return () => document.body.classList.remove("notif-panel-open");
  }, [open]);

  const toggle = async () => {
    if (!open) {
      setLoadError(false);
      try {
        const res = await api.get("/notifications");
        setItems(res.data);
      } catch {
        setItems(null);
        setLoadError(true);
      }
    }
    setOpen((o) => !o);
  };

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      try {
        await api.patch(`/notifications/${n.id}/read`);
      } catch {}
      refreshCount();
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await api.patch("/notifications/read-all");
      setItems((list) => (list || []).map((n) => ({ ...n, read: true })));
      setCount(0);
      prevCount.current = 0;
    } catch {}
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundEnabled(next);
    setSoundOn(next);
    if (next && hasUserInteracted()) playNotificationChime();
  };

  const onEnablePush = async () => {
    setPushBusy(true);
    setPushError("");
    const res = await enablePush();
    if (res.ok) {
      setPush({ support: "supported", subscribed: true });
    } else if (res.reason === "denied") {
      setPush({ support: "denied", subscribed: false });
    } else if (res.reason === "unsupported") {
      setPush({ support: "unsupported", subscribed: false });
    } else {
      setPushError("Could not enable browser notifications. Please try again.");
    }
    setPushBusy(false);
  };

  const onDisablePush = async () => {
    setPushBusy(true);
    setPushError("");
    const res = await disablePush();
    if (res.ok) {
      setPush((p) => ({ ...p, subscribed: false }));
    } else {
      setPushError("Could not disable browser notifications. Please try again.");
    }
    setPushBusy(false);
  };

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        onClick={toggle}
        className="relative inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-[#94A3B8] hover:text-[#D4AF37] transition-colors"
        data-testid="notification-bell-button"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center"
            data-testid="notification-badge"
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} data-testid="notification-panel-overlay" />
          <div className="notif-panel absolute right-0 mt-2 w-[22rem] max-w-[calc(100vw-1rem)] max-h-[calc(100dvh-6.5rem)] z-[61] card-lux overflow-hidden flex flex-col" data-testid="notification-panel">
            {/* Header — compact, stays fixed while the list scrolls */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#222530] shrink-0">
              <p className="text-base font-semibold text-white tracking-tight">Notifications</p>
              <div className="flex items-center gap-0.5">
                {items && items.some((n) => !n.read) && (
                  <button onClick={markAll} className="text-[11px] text-[#D4AF37] hover:text-[#E5C158] inline-flex items-center gap-1 px-2 py-1 transition-colors" data-testid="notification-mark-all-read">
                    <CheckCheck className="h-3.5 w-3.5" /> Read all
                  </button>
                )}
                <button onClick={toggleSound} className="inline-flex items-center justify-center h-9 w-9 text-[#64748B] hover:text-[#D4AF37] transition-colors" aria-label={soundOn ? "Turn notification sound off" : "Turn notification sound on"} data-testid="notification-sound-toggle">
                  {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
                <button onClick={() => setOpen(false)} className="inline-flex items-center justify-center h-9 w-9 text-[#94A3B8] hover:text-white transition-colors" aria-label="Close notifications" data-testid="notification-close">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Enable control — directly below header, above the list, stays fixed */}
            <div className="px-3 pt-3 shrink-0" data-testid="notification-preferences">
              {push.support === "supported" && !push.subscribed && (
                <div className="flex items-center gap-3 rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.03] px-3 py-2.5">
                  <BellRing className="h-5 w-5 text-[#D4AF37] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-white leading-tight">Enable notifications</p>
                    <p className="text-[11px] text-[#64748B] leading-tight truncate">Get the latest updates from PlusMaker.</p>
                  </div>
                  <button onClick={onEnablePush} disabled={pushBusy} className="shrink-0 rounded-lg border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs px-3 py-1.5 transition-colors disabled:opacity-50" data-testid="notification-push-enable">
                    {pushBusy ? "..." : "Enable"}
                  </button>
                </div>
              )}
              {push.support === "supported" && push.subscribed && (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-[#222530] px-3 py-2">
                  <p className="text-[11px] text-[#94A3B8]">Browser notifications on.</p>
                  <button onClick={onDisablePush} disabled={pushBusy} className="shrink-0 text-[11px] text-[#64748B] hover:text-white transition-colors disabled:opacity-50" data-testid="notification-push-disable">{pushBusy ? "..." : "Disable"}</button>
                </div>
              )}
              {push.support === "denied" && (
                <p className="text-[11px] text-amber-400/90 px-1 py-1" data-testid="notification-push-denied">Browser notifications are blocked. Enable them in your browser's site settings to receive updates.</p>
              )}
              {push.support === "unsupported" && (
                <p className="text-[11px] text-[#64748B] px-1 py-1" data-testid="notification-push-unsupported">This browser does not support notifications. You'll still see every update here.</p>
              )}
              {pushError && <p className="text-[11px] text-red-400 px-1 py-1" data-testid="notification-push-error">{pushError}</p>}
            </div>

            {/* List — the only scrolling region; thin dividers, compact rows */}
            <div className="notif-list mt-2 flex-1 min-h-0 overflow-y-auto divide-y divide-[#1b1e27] border-t border-[#222530]">
              {loadError ? (
                <p className="px-5 py-8 text-center text-xs text-red-400" data-testid="notification-error">Could not load notifications. Please try again.</p>
              ) : items === null ? (
                <p className="px-5 py-8 text-center text-xs text-[#64748B]" data-testid="notification-loading">Loading...</p>
              ) : items.length === 0 ? (
                <p className="px-5 py-8 text-center text-xs text-[#64748B]" data-testid="notification-empty">No notifications yet.</p>
              ) : (
                items.map((n) => (
                  <div
                    key={n.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openItem(n)}
                    onKeyDown={(e) => e.key === "Enter" && openItem(n)}
                    className={`flex items-center gap-3 px-3 py-3 cursor-pointer transition-colors hover:bg-[#0B0C10]/70 ${n.read ? "" : "bg-[#D4AF37]/[0.035]"}`}
                    data-testid={`notification-item-${n.id.slice(-6)}`}
                  >
                    {n.image ? (
                      <img loading="lazy" src={`${API}${n.image}`} alt="" className="h-12 w-12 rounded-lg object-cover border border-[#222530] shrink-0" data-testid={`notification-thumb-${n.id.slice(-6)}`} />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0" data-testid={`notification-thumb-fallback-${n.id.slice(-6)}`}>
                        <Megaphone className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className={`text-[13px] leading-snug line-clamp-2 ${n.read ? "text-[#94A3B8] font-normal" : "text-white font-semibold"}`}>{n.title}</p>
                      {n.body && <p className="mt-0.5 text-[11px] text-[#64748B] leading-snug truncate">{n.body}</p>}
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37]">{n.indicator_name || (n.kind === "news" ? "News" : "Update")}</span>
                        <span className="text-[10px] text-[#475569]">·</span>
                        <span className="text-[10px] text-[#64748B] font-mono">{n.created_at ? fmtMYT(n.created_at) : ""}</span>
                      </div>
                    </div>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-[#D4AF37] shrink-0" data-testid={`notification-unread-dot-${n.id.slice(-6)}`} />}
                    <ChevronRight className="h-4 w-4 text-[#475569] shrink-0" />
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
